import Button from "components/Button";
import { TextInput } from "components/Input";
import Modal from "components/Modal";
import React, { useCallback } from "react";
import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import styles from "./styles.module.scss";

interface Props {
  data: {
    address: string;
    electricBill: string;
    email: string;
    name: string;
    phone: string;
  };
  error?: string;
  onChange: (data: {
    address: string;
    electricBill: string;
    email: string;
    name: string;
    phone: string;
  }) => void;
  onClose?: () => void;
  onError?: (error: string) => void;
  onSuccess?: (data: { range: string; size: number }) => void;
}

const QuotationInput: React.FC<Props> = ({
  data,
  error,
  onChange,
  onClose = () => null,
  onError = () => null,
  onSuccess = () => null,
}) => {
  const handleChange = useCallback(
    (key: string, value: string) => {
      onError("");
      onChange({ ...data, [key]: value });
    },
    [data, onChange, onError]
  );

  const handleElectricBillChange = useCallback(
    (value: string) => {
      const result = value.replace(/[^0-9.]/g, "");
      const electricBill = result.replace(
        /(.*?\.)(.*)/,
        (_, firstPart, rest) => {
          return firstPart + rest.replace(/\./g, "");
        }
      );

      handleChange("electricBill", electricBill);
    },
    [handleChange]
  );

  const calculateEstimatedPrice = useCallback(
    (electricBill: number) => {
      const size = 0.00136 * electricBill + 0.2658;

      let range = "unknown";
      if (size >= 1 && size < 3) {
        range = "90k to 120k";
      } else if (size >= 3 && size < 5) {
        range = "120k to 150k";
      } else if (size >= 5 && size < 8) {
        range = "150k to 200k";
      } else if (size >= 8 && size < 10) {
        range = "200k to 250k";
      } else if (size >= 10 && size < 13) {
        range = "250k to 300k";
      } else if (size >= 13 && size < 15) {
        range = "300k to 350k";
      } else if (size >= 15 && size < 20) {
        range = "350k to 400k";
      } else if (size >= 20 && size < 30) {
        range = "400k to 500k";
      } else if (size >= 30 && size < 40) {
        range = "500k to 600k";
      }

      if (range !== "unknown") {
        onSuccess({ size, range });
      } else {
        onError(
          "Unable to Calculate Quotation. The electric bill amount you've entered is either too small or too large for us to process. Please contact us directly, and our team will gladly assist you with a personalized solution."
        );
      }
    },
    [onError, onSuccess]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (Object.values(data).some((value) => !value)) {
        return onError("Please complete all fields.");
      }

      if (!isEmail(data.email)) {
        return onError("Please enter a valid email.");
      }

      let phoneNumber = data.phone;
      if (phoneNumber.startsWith("09")) {
        phoneNumber = "+63" + phoneNumber.slice(1);
      } else if (!phoneNumber.startsWith("+")) {
        phoneNumber = "+" + phoneNumber;
      }

      if (!isMobilePhone(phoneNumber)) {
        return onError("Please enter a valid phone number.");
      }

      const electricBill = parseFloat(data.electricBill);
      if (!electricBill) {
        return onError("Please enter a valid electricity bill.");
      }

      onError("");
      calculateEstimatedPrice(electricBill);

      try {
        const url = process.env.REACT_APP_GOOGLE_SHEET_URL || "";

        await fetch(url, {
          method: "POST",
          body: new URLSearchParams({
            name: data.name,
            email: data.email,
            address: data.address,
            phoneNumber,
            electricBill: electricBill.toString(),
          }),
        });
      } catch {
        // NO OP
      }
    },
    [calculateEstimatedPrice, data, onError]
  );

  return (
    <Modal
      className={styles.input}
      contentClassName={styles.content}
      footer={
        <>
          <Button
            className={styles.footerButton}
            onClick={onClose}
            type="light"
          >
            Cancel
          </Button>

          <Button className={styles.footerButton} type="primary" isSubmit>
            Submit
          </Button>
        </>
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Free Quotation"
      visible
    >
      <TextInput
        label="Name"
        maxLength={50}
        onChange={(value) => handleChange("name", value)}
        placeholder="Juan Dela Cruz"
        value={data.name}
      />

      <div className={styles.row}>
        <TextInput
          label="Email"
          maxLength={50}
          onChange={(value) => handleChange("email", value)}
          placeholder="example@company.com"
          value={data.email}
        />

        <TextInput
          label="Phone"
          maxLength={20}
          onChange={(value) => handleChange("phone", value)}
          placeholder="+639123456789"
          value={data.phone}
        />
      </div>

      <TextInput
        label="House Address"
        maxLength={100}
        onChange={(value) => handleChange("address", value)}
        placeholder="123 Main Street, City, Province"
        value={data.address}
      />

      <TextInput
        label="Average Monthly Electric Bill (Php)"
        maxLength={10}
        onChange={handleElectricBillChange}
        placeholder="5000"
        value={data.electricBill}
      />

      <div className={styles.error}>{error}</div>
    </Modal>
  );
};

export default QuotationInput;
